import React, { useState } from "react";
import { BellRing, ChevronDown, ChevronUp, RefreshCw, ShieldCheck, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";

const NotificationPermissionPrompt = ({
  permission: propPermission,
  onRequest,
  onCheckStatus,
  compact = false,
}) => {
  const [showGuide, setShowGuide] = useState(false);
  const [checking, setChecking] = useState(false);

  const permission = propPermission || (typeof window !== "undefined" && "Notification" in window ? Notification.permission : "unsupported");
  const unsupported = permission === "unsupported";
  const granted = permission === "granted";
  const denied = permission === "denied";

  const handleRecheck = async () => {
    setChecking(true);
    try {
      if (onCheckStatus) {
        await onCheckStatus();
      } else {
        window.location.reload();
      }
    } finally {
      setChecking(false);
    }
  };

  return (
    <div
      className={`rounded-2xl border p-4 transition ${
        granted
          ? "border-emerald-200 bg-emerald-50/80 text-emerald-950"
          : denied
            ? "border-amber-300 bg-amber-50/90 text-amber-950"
            : "border-primary/30 bg-primary/5 text-slate-950"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${
            granted
              ? "bg-emerald-100 text-emerald-700"
              : denied
                ? "bg-amber-200/70 text-amber-800"
                : "bg-primary/10 text-primary"
          }`}
        >
          {granted ? (
            <ShieldCheck className="h-6 w-6" />
          ) : denied ? (
            <TriangleAlert className="h-6 w-6" />
          ) : (
            <BellRing className="h-6 w-6" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="font-semibold text-base">
            {granted
              ? "Notifications & call alerts are active"
              : denied
                ? "Notifications are blocked by your browser"
                : unsupported
                  ? "Notifications are not supported in this browser"
                  : "Enable notifications & call alerts"}
          </p>

          <p className="mt-1 text-sm text-slate-600">
            {granted
              ? "Your device will receive live guest calls, department ringers, and background PWA push alerts."
              : denied
                ? "Your browser is currently blocking notification permissions for this website. Follow the steps below to unblock it."
                : unsupported
                  ? "Use Safari, Chrome, Edge, or Firefox with service worker support to receive call notifications."
                  : "Grant notification permissions so incoming guest calls and department ringers reach your device automatically."}
          </p>

          {!granted && !unsupported && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {!denied && (
                <Button
                  type="button"
                  className="touch-target gap-2 rounded-xl bg-primary text-white font-medium hover:bg-primary/90"
                  onClick={onRequest}
                >
                  <BellRing className="h-4 w-4" />
                  Grant call permissions
                </Button>
              )}

              {denied && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    className="touch-target gap-2 rounded-xl border-amber-300 bg-white font-medium text-amber-900 hover:bg-amber-100/50"
                    onClick={() => setShowGuide(!showGuide)}
                  >
                    {showGuide ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    {showGuide ? "Hide unblock steps" : "How to unblock in browser"}
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="touch-target gap-2 rounded-xl text-amber-900 hover:bg-amber-100/50"
                    onClick={handleRecheck}
                    disabled={checking}
                  >
                    <RefreshCw className={`h-4 w-4 ${checking ? "animate-spin" : ""}`} />
                    Re-check permission
                  </Button>
                </>
              )}
            </div>
          )}

          {denied && showGuide && (
            <div className="mt-3 rounded-xl border border-amber-200 bg-white p-3 text-xs text-slate-800 space-y-2">
              <p className="font-semibold text-slate-900 text-sm">How to unblock notifications:</p>
              
              <div className="space-y-1">
                <p className="font-medium text-slate-900">🍏 For Safari (macOS / iOS):</p>
                <ol className="list-decimal list-inside space-y-0.5 text-slate-600 pl-1">
                  <li>Click <strong>Safari</strong> in the top menu bar (or tap the <strong>aA</strong> / site settings icon in address bar).</li>
                  <li>Select <strong>Settings for This Website...</strong></li>
                  <li>Change <strong>Notifications</strong> from <em>Deny</em> to <strong>Allow</strong>.</li>
                  <li>Refresh this page.</li>
                </ol>
              </div>

              <div className="pt-1 space-y-1 border-t border-slate-100">
                <p className="font-medium text-slate-900">🌐 For Chrome / Edge / Firefox:</p>
                <ol className="list-decimal list-inside space-y-0.5 text-slate-600 pl-1">
                  <li>Click the <strong>Lock / Tune icon 🔒</strong> next to the web address (<code className="bg-slate-100 px-1 py-0.5 rounded text-slate-900">dev-hotels...</code>).</li>
                  <li>Toggle <strong>Notifications</strong> to <strong>Allow</strong> (or open <em>Site settings</em>).</li>
                  <li>Refresh this page.</li>
                </ol>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationPermissionPrompt;
